import React, { useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import ListGroup from "../../components/ListGroup";
import { capitalizeFirstLetter, formatCurrency } from "../../utils";
import { useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import { useQuery } from "@apollo/client";
import { FIND_MY_SALARIES } from "../../graphql/salaries";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import NoSalariesImage from "../../assets/images/no-salaries-image.svg";
import EmptyScreen from "../../components/EmptyScreen";
import ScreenCard from "../ScreenCard";
import { useDateFormatter } from "../../hooks/date";

type ScreenParams = StackScreenProps<RootStackParamList>;

const Salaries = () => {
    const i18n = useI18n();
    const navigator = useNavigation<ScreenParams["navigation"]>();

    const { loading, error, data, refetch } = useQuery(FIND_MY_SALARIES, {
        fetchPolicy: "cache-and-network",
    });

    const mySalaries = useMemo(
        () => data?.findMySalaries.elements || [],
        [data?.findMySalaries.elements],
    );

    const intlFormat = useDateFormatter();

    const items = useMemo(() => {
        return (mySalaries || []).map((salary) => {
            return {
                title: capitalizeFirstLetter(
                    intlFormat(new Date(salary.year, salary.month - 1), {
                        year: "numeric",
                        month: "long",
                    }),
                ),
                value: `${salary.year}-${salary.month}`,
                description: formatCurrency({ amountInCents: salary.netAmount }),
                tags: [
                    salary.paidAt
                        ? capitalizeFirstLetter(
                              intlFormat(new Date(salary.paidAt), {
                                  day: "numeric",
                                  year: "numeric",
                                  month: "long",
                              }),
                          )
                        : "",
                ],
                onPress: () => {
                    navigator.navigate("SalaryDetails", {
                        salaryId: salary.id,
                    });
                },
            };
        });
    }, [intlFormat, mySalaries, navigator]);

    return (
        <ScreenCard refresh={refetch} isContentContainerFlex={items.length === 0}>
            <LoadingBoundary
                error={error ? i18n.t("screens.user.salaries.cannot_load_salaries") : undefined}
                loading={loading && !data}
            >
                {items.length === 0 ? (
                    <EmptyScreen
                        image={NoSalariesImage}
                        text={i18n.t("screens.user.salaries.no_salary")}
                    />
                ) : (
                    <ListGroup items={items} />
                )}
            </LoadingBoundary>
        </ScreenCard>
    );
};

export default Salaries;
